/**
 * Smart BOM versions — the shapes served by buyer-service
 * `/trp/api/bom/:bomId/versions*` (reached via the orchestrator as
 * `/api/trp/bom/...`). Snapshots arrive client-safe: supplier candidates
 * carry `hasContactEmail`, never an address.
 */
import { BomCompleteness } from './bom.types';

export type BomVersionTrigger =
  | 'EXTRACTED'
  | 'CONTEXT_APPLIED'
  | 'VALIDATION_FIX'
  | 'COMPONENT_REPLACED'
  | 'COMPONENT_REVERTED'
  | 'SHORTLIST_UPDATED'
  | 'MANUAL_EDIT'
  | 'CREDIT_SELECTION';

export const TRIGGER_LABELS: Record<BomVersionTrigger, string> = {
  EXTRACTED: 'Extracted',
  CONTEXT_APPLIED: 'Context applied',
  VALIDATION_FIX: 'Validation fix',
  COMPONENT_REPLACED: 'Component replaced',
  COMPONENT_REVERTED: 'Component reverted',
  SHORTLIST_UPDATED: 'Shortlist updated',
  MANUAL_EDIT: 'Manual edit',
  // Lines the buyer left out when paying for discovery at the step-2 exit.
  CREDIT_SELECTION: 'Left out of discovery',
};

/**
 * Who a version is credited to. `robbie` = Robbie proposed it and the buyer
 * in `createdBy` approved it; `system` = the extracted baseline. null on
 * versions recorded before actors existed — read by `createdBy` alone.
 */
export type BomVersionActor = 'buyer' | 'robbie' | 'system';

export interface BomVersionMeta {
  version: number;
  trigger: BomVersionTrigger;
  summary: string;
  changeCount: number;
  componentCount: number;
  createdBy: string | null;
  /** Absent or null on versions recorded before actors existed. */
  actor?: BomVersionActor | null;
  /** The buyer's name at capture, when their token carried one. */
  actorName?: string | null;
  createdAt: string;
}

export interface BomVersionChange {
  componentId: string | null;
  componentName: string | null;
  field: string | null;
  before: unknown;
  after: unknown;
  reason: string | null;
}

/** One component as snapshotted at capture (client-safe shape). */
export interface BomVersionComponent {
  componentId?: string;
  name?: string;
  /** The buyer's own item code. */
  partNumber?: string;
  /** Manufacturer part number. */
  mpn?: string;
  description?: string;
  specifications?: string;
  quantity?: number;
  manufacturer?: string;
  targetPrice?: number;
  currency?: string;
  /** `robbie_suggestion` = added from Robbie's suggestion; absent otherwise. */
  origin?: BomLineOrigin;
  supplierCandidates?: Array<{
    supplierName?: string;
    status?: string;
    hasContactEmail?: boolean;
    [key: string]: unknown;
  }>;
  [key: string]: unknown;
}

/** How a line entered the BOM, when not from the buyer's document. */
export type BomLineOrigin = 'robbie_suggestion';

/** One Reasoning-sheet row, formatted server-side (shared with the export). */
export interface BomReasoningRow {
  version: number;
  date: string;
  trigger: BomVersionTrigger;
  actor?: BomVersionActor | null;
  actorName?: string | null;
  createdBy?: string | null;
  componentName: string;
  /** The change as one line ("quantity: 2 → 3") — the fallback rendering. */
  change: string;
  /** Structured parts of the change; absent from older servers. */
  field?: string | null;
  before?: unknown;
  after?: unknown;
  why: string;
}

export interface BomVersionDetail extends BomVersionMeta {
  bomId: string;
  changes: BomVersionChange[];
  components: BomVersionComponent[];
  reasoning: BomReasoningRow[];
}

/**
 * Manual-edit request row: an existing line plus the fields to change. A field
 * left out is left alone; `null` clears it (`name` and `quantity` cannot be
 * cleared).
 */
export interface SmartBomComponentEdit {
  componentId: string;
  name?: string;
  partNumber?: string | null;
  manufacturer?: string | null;
  mpn?: string | null;
  description?: string | null;
  specifications?: string | null;
  quantity?: number;
  targetPrice?: number | null;
}

/** A new line — `name` and a whole `quantity` >= 1 are required. */
export interface BomLineAdd {
  name: string;
  quantity: number;
  partNumber?: string;
  manufacturer?: string;
  mpn?: string;
  description?: string;
  specifications?: string;
  targetPrice?: number;
}

/** Who proposed the edit — sets the reason on every change of the version. */
export type ManualEditSource = 'buyer' | 'robbie';

/**
 * `POST /trp/bom/:bomId/versions/manual-edit` body. Applied edits → removes →
 * adds as ONE `MANUAL_EDIT` version; at least one list must be non-empty.
 */
export interface ManualEditRequest {
  components?: SmartBomComponentEdit[];
  adds?: BomLineAdd[];
  /** componentIds */
  removes?: string[];
  source?: ManualEditSource;
}

export interface ManualEditResult {
  noChanges: boolean;
  /** null when noChanges */
  version: number | null;
  /** New lines' ids, in request order. */
  added: Array<{ componentId: string; name: string }>;
  /** componentIds */
  removed: string[];
  /** The live BOM after the edit. */
  components: BomVersionComponent[];
  completeness: BomCompleteness;
}
